// Zentraler Zustand der Projektdaten mit Rückgängig/Wiederholen.
// Gespeichert wird nicht hier, sondern im Abgleich mit der API (src/sync.ts), wenn der Anwender „Speichern“ wählt.
import { useSyncExternalStore } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Daten = any;

const MAX_HISTORY = 120;

interface Zustand {
  data: Daten;
  past: Daten[];
  future: Daten[];
  dirty: boolean; // ungespeicherte Änderungen gegenüber dem Server
  lastKey: string | null;
  lastAt: number;
  version: number;
}

const state: Zustand = { data: null, past: [], future: [], dirty: false, lastKey: null, lastAt: 0, version: 0 };
const listeners = new Set<() => void>();

const emit = () => {
  state.version++;
  for (const l of listeners) l();
};

export const getState = () => state;
export const getData = () => state.data;

/** Neue Daten setzen (Projekt geöffnet). Löscht den Verlauf. */
export function load(data: Daten, { dirty = false } = {}) {
  Object.assign(state, { data, past: [], future: [], dirty, lastKey: null });
  emit();
}

/**
 * Änderung durchführen. fn bekommt einen Klon der Daten und darf ihn verändern.
 * Gleicher coalesce-Schlüssel innerhalb von 1,5 s = ein Rückgängig-Schritt (Tippen in Formularen).
 */
export function update<T>(fn: (d: Daten) => T, { coalesce = null as string | null } = {}): T {
  const before = state.data;
  const draft = structuredClone(before);
  const res = fn(draft);
  const now = Date.now();
  const merge = coalesce && coalesce === state.lastKey && now - state.lastAt < 1500;
  if (!merge) {
    state.past.push(before);
    if (state.past.length > MAX_HISTORY) state.past.shift();
  }
  Object.assign(state, { future: [], lastKey: coalesce, lastAt: now, data: draft, dirty: true });
  emit();
  return res;
}

export function undo() {
  if (!state.past.length) return;
  state.future.push(state.data);
  Object.assign(state, { data: state.past.pop(), lastKey: null, dirty: true });
  emit();
}

export function redo() {
  if (!state.future.length) return;
  state.past.push(state.data);
  Object.assign(state, { data: state.future.pop(), lastKey: null, dirty: true });
  emit();
}

/**
 * Daten ohne Rückgängig-Schritt ersetzen (Abgleich mit dem Server). Kamen fremde Änderungen hinzu,
 * wird der Verlauf geleert – sonst würde Rückgängig sie beim nächsten Speichern wieder entfernen.
 */
export function replaceData(data: Daten, { dirty = state.dirty, resetHistory = false } = {}) {
  state.data = data;
  state.dirty = dirty;
  if (resetHistory) Object.assign(state, { past: [], future: [], lastKey: null });
  emit();
}

export function markSaved() {
  state.dirty = false;
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** React-Hook: Komponente rendert bei jeder Änderung neu. */
export function useStore() {
  useSyncExternalStore(subscribe, () => state.version);
  return state;
}
